import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import api from "../lib/api";

/**
 * Unsplash hero image with proper photographer + Unsplash attribution
 * (required by their API guidelines). Cached server-side for 30 days.
 * Renders nothing while loading and silently falls back to no image if the
 * Unsplash quota is exhausted, so pages never break.
 *
 * Usage:
 *   <UnsplashHero query="Kerala backwaters" alt="…" eyebrow="Travel" title="…" subtitle="…" />
 */
export default function UnsplashHero({
  query,
  alt = "",
  eyebrow,
  title,
  subtitle,
  height = "h-64 sm:h-80 md:h-[26rem]",
  testid = "unsplash-hero",
}) {
  const [photo, setPhoto] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/unsplash/photos", { params: { q: query } });
        const p = (data?.photos || [])[0];
        if (!cancelled) setPhoto(p || null);
        // Unsplash guideline: fire a download event when the photo is used on the page.
        if (p?.download_location) {
          api.post("/unsplash/download", { download_location: p.download_location }).catch(() => {});
        }
      } catch { /* silent — hero degrades gracefully */ }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, [query]);

  // Header-only fallback (no image) so layout doesn't collapse when quota is out.
  if (!photo && ready && (title || subtitle || eyebrow)) {
    return (
      <header className="bg-zinc-900 text-white py-10 sm:py-14 px-5" data-testid={`${testid}-fallback`}>
        <div className="max-w-5xl mx-auto">
          {eyebrow && <div className="text-xs font-mono uppercase tracking-widest text-emerald-300 mb-2">{eyebrow}</div>}
          {title && <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight">{title}</h1>}
          {subtitle && <p className="mt-3 text-lg sm:text-xl text-zinc-300 max-w-3xl">{subtitle}</p>}
        </div>
      </header>
    );
  }
  if (!photo) return null;

  return (
    <header
      className={`relative ${height} bg-zinc-900 overflow-hidden`}
      data-testid={testid}
    >
      <img
        src={photo.image}
        alt={alt || photo.alt || query}
        loading="eager"
        className="absolute inset-0 w-full h-full object-cover opacity-70"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-900/40 to-zinc-900/10" />
      <div className="relative max-w-5xl mx-auto h-full flex items-end px-5 pb-8 sm:pb-10">
        <div className="text-white">
          {eyebrow && <div className="text-xs font-mono uppercase tracking-widest text-emerald-300 mb-2">{eyebrow}</div>}
          {title && <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tight">{title}</h1>}
          {subtitle && <p className="mt-3 text-lg sm:text-xl text-zinc-100 max-w-3xl">{subtitle}</p>}
        </div>
      </div>

      {/* Attribution — required by Unsplash API guidelines */}
      <div
        className="absolute bottom-2 right-2 text-[11px] text-white/90 bg-black/40 backdrop-blur-sm px-2 py-1 flex items-center gap-1 print:hidden"
        data-testid={`${testid}-attribution`}
      >
        Photo by{" "}
        <a
          href={photo.photographer_url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-emerald-300"
          data-testid={`${testid}-photographer`}
        >
          {photo.photographer_name}
        </a>
        {" on "}
        <a
          href={photo.photo_url}
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-emerald-300 inline-flex items-center gap-0.5"
          data-testid={`${testid}-unsplash`}
        >
          Unsplash <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </header>
  );
}
